import { MappedStakeholder, StakeholderRoleEnum } from "src/core/constants/folder";
import { AddStakeholderBasicFormValues } from "src/core/forms/stakeholder/addStakeholderBasic.schema";
import { UpdateStakeholderFullFormValues } from "src/core/forms/stakeholder/updateStakeholderFull.schema";
import {
  AddStakeholderMutationVariables,
  DataType,
  InputValueInput,
  UpdateStakeholderMutationVariables,
} from "src/core/gql/graphql";
import { parseDateInputAsLocalDate } from "src/core/utils/date";

export const buildCreateStakeholderVariables = (
  corporationId: string,
  data: AddStakeholderBasicFormValues,
  extraInputValues?: InputValueInput[]
): AddStakeholderMutationVariables => {
  const extras = extraInputValues ?? [];

  const hasNameExtra = extras.some((iv) => iv.fieldCode === "name");
  const shouldUseNameInsteadOfFirstLast =
    data.firstName === "" && data.lastName === "" && hasNameExtra;

  const nameInput = extras.find((iv) => iv.fieldCode === "name");

  const baseInputValues: InputValueInput[] = shouldUseNameInsteadOfFirstLast
    ? [
        {
          dataType: nameInput?.dataType ?? DataType.String,
          fieldCode: "name",
          value: nameInput?.value ?? "",
        },
      ]
    : [
        { dataType: DataType.String, fieldCode: "lastName", value: data.lastName },
        { dataType: DataType.String, fieldCode: "firstName", value: data.firstName },
      ];

  const baseFieldCodes = new Set(baseInputValues.map((iv) => iv.fieldCode));
  const mergedExtraInputValues = extras.filter((iv) => !baseFieldCodes.has(iv.fieldCode));

  const base: AddStakeholderMutationVariables = {
    reference_0: "<NEW_STAKEHOLDER>",
    inputValues_0: [...baseInputValues, ...mergedExtraInputValues],

    // Ces champs doivent toujours exister pour le typage, même s'ils ne seront jamais utilisés
    inputRole_1: { typeCode: "ignored_role" },
    corporationId_1: corporationId,
    entityId_1: "<NEW_STAKEHOLDER>",

    inputRole_2: {
      typeCode: "ignored_role",
      directBeneficiaryOwnerInput: {
        directBOReasonCode: "default",
        share: null,
        votingRights: null,
      },
    },

    includeLegalRep: false,
    includeDirectBO: false,
  };

  if (data.roles.includes(StakeholderRoleEnum.legal_representative)) {
    base.inputRole_1 = { typeCode: "legal_representative" };
    base.includeLegalRep = true;
  }

  if (data.roles.includes(StakeholderRoleEnum.direct_beneficiary_owner)) {
    base.inputRole_2 = {
      typeCode: "direct_beneficiary_owner",
      directBeneficiaryOwnerInput: {
        directBOReasonCode: "default",
        share: null,
        votingRights: null,
      },
    };
    base.includeDirectBO = true;
  }

  return base;
};

/**
 * Construit l'ensemble des variables nécessaires à la mutation GraphQL de mise à jour d'une partie prenante..
 *
 * Cette fonction :
 * - extrait et transforme dynamiquement les champs du formulaire (ex: formatage des dates)
 * - génère les `inputValues` attendus par la mutation à partir des champs renseignés
 * - calcule les rôles à ajouter ou supprimer en comparant les rôles sélectionnés dans le formulaire
 *   avec l'état actuel de l'associé (`isLegalRepresentative`, `isBeneficiaryOwner`)
 * - retourne un objet complet conforme au type `UpdateStakeholderMutationVariables`,
 *   incluant les structures `inputRole`, les flags `add/deleteLegalRep` et `add/deleteDirectBO`
 *
 * Le fait que certains champs n'aient pas de valeur permet à GraphQL de les supprimer.
 */
export const buildUpdateStakeholderVariables = (
  corporationId: string,
  selectedStakeholder: MappedStakeholder,
  data: UpdateStakeholderFullFormValues
): UpdateStakeholderMutationVariables => {
  const hasSameBirthDate = (left?: string, right?: string): boolean => {
    if (!left && !right) {
      return true;
    }

    if (!left || !right) {
      return false;
    }

    const leftDate = parseDateInputAsLocalDate(left);
    const rightDate = parseDateInputAsLocalDate(right);

    if (!leftDate || !rightDate) {
      return left === right;
    }

    return (
      leftDate.getFullYear() === rightDate.getFullYear() &&
      leftDate.getMonth() === rightDate.getMonth() &&
      leftDate.getDate() === rightDate.getDate()
    );
  };

  const isIdentityUnchanged =
    data.firstName === selectedStakeholder.firstName &&
    data.lastName === selectedStakeholder.lastName &&
    hasSameBirthDate(data.birthDate, selectedStakeholder.birthDate);

  const fieldDescriptors: {
    fieldCode: string;
    dataType: DataType;
    value?: string;
    transform?: (
      value: UpdateStakeholderFullFormValues[keyof UpdateStakeholderFullFormValues]
    ) => string;
  }[] = [
    { fieldCode: "lastName", dataType: DataType.String },
    { fieldCode: "firstName", dataType: DataType.String },
    { fieldCode: "title", dataType: DataType.String },
    { fieldCode: "birthName", dataType: DataType.String },
    { fieldCode: "citizenshipCountryCode", dataType: DataType.Code },
    {
      fieldCode: "birthDate",
      dataType: DataType.Timestamp,
      // hack pour que la date de naissance soit au format UTC
      transform: (val) => (typeof val === "string" ? val.replace(/\+\d{2}:\d{2}/, "Z") : ""),
    },
    { fieldCode: "residenceCountryCode", dataType: DataType.Code },
    { fieldCode: "birthCountryCode", dataType: DataType.Code },
    { fieldCode: "birthPlace", dataType: DataType.String },
    /**
     * Si l'identité a changée (nom, prénom ou date de naissance),
     * on met le flag à "false", afin de notifier qu'il faut revérifier les doublons.
     */
    {
      fieldCode: "customBoolean1",
      dataType: DataType.Boolean,
      value: isIdentityUnchanged ? "true" : "false",
    },
  ];

  const inputValues_0: InputValueInput[] = fieldDescriptors.map(
    ({ fieldCode, dataType, value: forcedValue, transform }) => {
      if (forcedValue !== undefined) {
        return { fieldCode, dataType, value: forcedValue };
      }

      const rawValue = data[fieldCode as keyof typeof data];
      if ((typeof rawValue !== "string" && typeof rawValue !== "number") || rawValue === "") {
        return { fieldCode, dataType };
      }

      const value = transform ? transform(rawValue) : rawValue;
      return { fieldCode, dataType, value };
    }
  );

  const roles = data.roles ?? [];
  const isLegalRepInForm = roles.includes(StakeholderRoleEnum.legal_representative);
  const isDirectBOInForm = roles.includes(StakeholderRoleEnum.direct_beneficiary_owner);

  const addLegalRep = isLegalRepInForm && !selectedStakeholder.isLegalRepresentative;
  const addDirectBO = isDirectBOInForm && !selectedStakeholder.isBeneficiaryOwner;
  const deleteLegalRep = !isLegalRepInForm && selectedStakeholder.isLegalRepresentative;
  const deleteDirectBO = !isDirectBOInForm && selectedStakeholder.isBeneficiaryOwner;

  const base: UpdateStakeholderMutationVariables = {
    id_0: selectedStakeholder.id,
    inputValues_0,

    // Ces champs doivent toujours exister pour le typage, même s'ils ne seront jamais utilisés
    inputRole_1: { typeCode: "ignored_role" },
    corporationId_1: corporationId,
    entityId_1: selectedStakeholder.id,

    inputRole_2: {
      typeCode: "ignored_role",
      directBeneficiaryOwnerInput: {
        directBOReasonCode: "default",
        share: null,
        votingRights: null,
      },
    },

    addLegalRep,
    addDirectBO,

    corporationId_2: corporationId,
    entityId_2: selectedStakeholder.id,

    deleteLegalRep,
    deleteDirectBO,
  };

  if (addLegalRep) {
    base.inputRole_1 = { typeCode: "legal_representative" };
    base.addLegalRep = true;
  }

  if (addDirectBO) {
    base.inputRole_2 = {
      typeCode: "direct_beneficiary_owner",
      directBeneficiaryOwnerInput: {
        directBOReasonCode: "default",
        share: null,
        votingRights: null,
      },
    };
    base.addDirectBO = true;
  }

  if (deleteLegalRep) {
    base.deleteLegalRep = true;
    base.corporationId_2 = corporationId;
    base.entityId_2 = selectedStakeholder.id;
  }

  if (deleteDirectBO) {
    base.deleteDirectBO = true;
    base.corporationId_2 = corporationId;
    base.entityId_2 = selectedStakeholder.id;
  }

  return base;
};
